import { useSnackbar } from 'notistack';
import { useState, useCallback, useMemo } from 'react';

import { Avatar, Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DragDropContext, Droppable, type DropResult } from '@hello-pangea/dnd';
import { Tree } from 'react-organizational-chart';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useTranslate } from 'src/locales';
import { TenantType } from 'src/types/enums';
import { useGetCompanyByIdQuery, useGetCompanyOrgChartQuery } from 'src/services/connections/connections.service';
import { useUpdateContactMutation } from 'src/services/contacts/contacts.service';
import EmptyContent from 'src/components/empty-content';
import ListSkeleton from 'src/components/list/list-skeleton';
import Scrollbar from 'src/components/scrollbar';
import RecordPanelFrame from 'src/components/record-panel/record-panel-frame';
import ContactRecordView from 'src/components/record-panel/views/contact-record-view';

import { buildOrgForest, collectDescendantIds, findNode } from './build-org-tree';
import { DraggableOrgCard, OrgChartBranch } from './org-chart-node';
import { OrgChartPreviewContext } from './org-chart-preview';

type Props = { companyProfileId: string };

// Dropping a card here detaches it from its manager (top-level / unassigned node).
const ROOT_DROPPABLE_ID = '__root__';

export default function CompanyOrgChart({ companyProfileId }: Props) {
  const { t } = useTranslate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const tenantType = useOrgTenant();
  // Recruiters own the contacts and can re-arrange reporting lines; client tenants view read-only.
  const canManage = tenantType === TenantType.Recruiter;

  const { currentData: company } = useGetCompanyByIdQuery({ profile_id: companyProfileId }, { skip: !companyProfileId });
  const { currentData: contacts, isLoading } = useGetCompanyOrgChartQuery({ profile_id: companyProfileId }, { skip: !companyProfileId });
  const [updateContact] = useUpdateContactMutation();

  // Contact opened by a card's eye — a local-state read-only quick peek over the chart (see the
  // RecordPanelFrame at the bottom of the render). Local state, not the URL record-panel singleton.
  const [previewContactId, setPreviewContactId] = useState<string | null>(null);

  const companyContacts = useMemo(() => contacts ?? [], [contacts]);
  const forest = useMemo(() => buildOrgForest(companyContacts), [companyContacts]);
  const connectedRoots = useMemo(() => forest.filter((n) => n.children.length > 0), [forest]);
  const unassigned = useMemo(() => forest.filter((n) => n.children.length === 0), [forest]);

  // Prev/next paging for the quick side-panel, walking the company's contacts in list order so the
  // peek behaves like the list quick-views (arrows + "N of M").
  const previewIndex = previewContactId ? companyContacts.findIndex((c) => c.id === previewContactId) : -1;
  const prevPreviewId = previewIndex > 0 ? companyContacts[previewIndex - 1].id : undefined;
  const nextPreviewId = previewIndex >= 0 && previewIndex < companyContacts.length - 1 ? companyContacts[previewIndex + 1].id : undefined;

  const handleDragEnd = useCallback(async (result: DropResult) => {
    const { draggableId: sourceId, destination } = result;
    if (!destination) return;
    const destId = destination.droppableId;
    if (destId === sourceId) return;

    let newManagerId: string | null;
    if (destId === ROOT_DROPPABLE_ID) {
      newManagerId = null;
    } else {
      const sourceNode = findNode(forest, sourceId);
      const descendants = sourceNode ? collectDescendantIds(sourceNode) : new Set<string>();
      if (descendants.has(destId)) {
        enqueueSnackbar(t('companies.org_chart.cycle_error'), { variant: 'error' });
        return;
      }
      newManagerId = destId;
    }

    const current = companyContacts.find((c) => c.id === sourceId)?.reports_to_contact_id ?? null;
    if (current === newManagerId) return;

    try {
      await updateContact({ contact_id: sourceId, reports_to_contact_id: newManagerId, company_profile_id: companyProfileId }).unwrap();
      enqueueSnackbar(t('companies.org_chart.change_manager_success'), { variant: 'success' });
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message ?? t('companies.org_chart.change_manager_error');
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [forest, companyContacts, companyProfileId, updateContact, enqueueSnackbar, t]);

  // The company node. When the viewer can manage, it is also a drop target that detaches
  // (clears the manager of) any card dropped onto it.
  const companyCard = (
    <Card variant="outlined" sx={{ display: 'inline-block', px: 2, py: 1, bgcolor: 'background.neutral' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar src={company?.company_logo?.public_path} sx={{ width: 28, height: 28, fontSize: 14 }}>
          {company?.company_name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Typography variant="subtitle1" noWrap>{company?.company_name ?? t('companies.org_chart.title')}</Typography>
      </Stack>
    </Card>
  );

  const companyNode = canManage ? (
    <Droppable droppableId={ROOT_DROPPABLE_ID}>
      {(dropProvided, dropSnapshot) => (
        <Box
          ref={dropProvided.innerRef} {...dropProvided.droppableProps}
          sx={{
            borderRadius: 1, p: 0.5,
            outline: dropSnapshot.isDraggingOver ? `2px dashed ${theme.palette.primary.main}` : '2px dashed transparent',
            transition: 'outline-color .15s',
          }}
        >
          {companyCard}
          <Box sx={{ display: 'none' }}>{dropProvided.placeholder}</Box>
        </Box>
      )}
    </Droppable>
  ) : companyCard;

  const chartBody = (
    <>
      <Scrollbar sx={{ pb: 2 }}>
        <Stack sx={{ minWidth: 'max-content', p: 2 }} alignItems="center">
          <Tree lineWidth="2px" lineColor={theme.palette.divider} lineBorderRadius="8px" label={companyNode}>
            {connectedRoots.map((root) => (
              <OrgChartBranch key={root.contact.id} node={root} companyContacts={companyContacts} companyProfileId={companyProfileId} canManage={canManage} />
            ))}
          </Tree>
        </Stack>
      </Scrollbar>

      {unassigned.length > 0 && (
        <>
          <Divider sx={{ mt: 2 }}>
            <Typography variant="overline" color="text.secondary">
              {t('companies.org_chart.unassigned', { count: unassigned.length })}
            </Typography>
          </Divider>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1, mb: 2 }}>
            {t('companies.org_chart.unassigned_hint')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {unassigned.map((node) => (
              <DraggableOrgCard key={node.contact.id} contact={node.contact} companyContacts={companyContacts} companyProfileId={companyProfileId} canManage={canManage} />
            ))}
          </Box>
        </>
      )}
    </>
  );

  return (
    <OrgChartPreviewContext.Provider value={setPreviewContactId}>
      <Card>
        <CardContent>
          {isLoading && <ListSkeleton />}

          {!isLoading && companyContacts.length === 0 && (
            <EmptyContent title={t('companies.org_chart.empty')} description={t('companies.org_chart.empty_description')} sx={{ py: 6 }} />
          )}

          {!isLoading && companyContacts.length > 0 && (
            canManage ? (
              <DragDropContext onDragEnd={handleDragEnd}>{chartBody}</DragDropContext>
            ) : (
              chartBody
            )
          )}
        </CardContent>
      </Card>

      {/* Read-only quick peek over the chart (eye icon). Local state, so it never touches the URL
          ?preview singleton and can coexist with a company overlay that hosts the chart. Pinned to
          zIndex.modal so it sits on TOP of that overlay (a Dialog at the same layer, opened earlier);
          `hideActions` keeps it action-free so nothing has to layer above it. */}
      <RecordPanelFrame
        variant="quick"
        open={!!previewContactId}
        onClose={() => setPreviewContactId(null)}
        onPrev={prevPreviewId ? () => setPreviewContactId(prevPreviewId) : undefined}
        onNext={nextPreviewId ? () => setPreviewContactId(nextPreviewId) : undefined}
        position={previewIndex >= 0 ? { index: previewIndex + 1, total: companyContacts.length } : undefined}
        zIndex={theme.zIndex.modal}
      >
        {previewContactId && <ContactRecordView id={previewContactId} hideActions />}
      </RecordPanelFrame>
    </OrgChartPreviewContext.Provider>
  );
}
